const KEY = 'apex_flow_tracked';
const SIGNALS_KEY = 'apex_flow_signals';
const MAX_STORED = 500;
function pip(pair) {
    if (pair.startsWith('BTC'))
        return 0.1;
    if (pair.startsWith('ETH'))
        return 1;
    if (pair.startsWith('BNB'))
        return 2;
    if (pair.startsWith('SOL'))
        return 10;
    if (pair.startsWith('XRP'))
        return 1000;
    if (pair.startsWith('ADA'))
        return 2000;
    if (pair.startsWith('DOT'))
        return 200;
    if (pair.startsWith('AVA'))
        return 100;
    if (pair.includes('JPY'))
        return 100;
    return 10_000;
}
export function calcPnlPips(signal, outcome) {
    const multiplier = pip(signal.pair);
    switch (outcome) {
        case 'tp1': return parseFloat((Math.abs(signal.tp1 - signal.entry) * multiplier).toFixed(1));
        case 'tp2': return parseFloat((Math.abs(signal.tp2 - signal.entry) * multiplier).toFixed(1));
        case 'tp3': return parseFloat((Math.abs(signal.tp3 - signal.entry) * multiplier).toFixed(1));
        case 'sl': return -parseFloat((Math.abs(signal.sl - signal.entry) * multiplier).toFixed(1));
        default: return 0;
    }
}
export function loadTracked() {
    try {
        return JSON.parse(localStorage.getItem(KEY) ?? '[]');
    }
    catch {
        return [];
    }
}
export function saveTracked(list) {
    localStorage.setItem(KEY, JSON.stringify(list));
}
export function addTracked(signal) {
    const list = loadTracked();
    if (list.find(t => t.signal.id === signal.id))
        return list;
    const updated = [{ signal, addedAt: Date.now(), outcome: 'pending' }, ...list];
    saveTracked(updated);
    return updated;
}
export function setOutcome(id, outcome, signal) {
    const list = loadTracked().map(t => t.signal.id === id
        ? { ...t, outcome, outcomeAt: Date.now(), pnlPips: calcPnlPips(signal, outcome) }
        : t);
    saveTracked(list);
    return list;
}
export function removeTracked(id) {
    const list = loadTracked().filter(t => t.signal.id !== id);
    saveTracked(list);
    return list;
}
export function clearTracked() {
    saveTracked([]);
    return [];
}
// ─── Signal feed persistence ──────────────────────────────────────────────────
export function loadSignals() {
    try {
        return JSON.parse(localStorage.getItem(SIGNALS_KEY) ?? '[]');
    }
    catch {
        return [];
    }
}
const SIGNAL_DEDUP_MS = 30 * 60 * 1000; // 30 min
export function persistSignal(signal) {
    const existing = loadSignals();
    // Reject exact duplicate
    if (existing.find(s => s.id === signal.id))
        return existing;
    // Reject if same pair signaled within the last 30 min (page refresh / TF change guard)
    const cutoff = Date.now() - SIGNAL_DEDUP_MS;
    if (existing.find(s => s.pair === signal.pair && s.timestamp > cutoff))
        return existing;
    const updated = [signal, ...existing].slice(0, MAX_STORED);
    localStorage.setItem(SIGNALS_KEY, JSON.stringify(updated));
    return updated;
}
export function clearSignals() {
    localStorage.removeItem(SIGNALS_KEY);
    return [];
}
export function calcStats(list) {
    const resolved = list.filter(t => t.outcome !== 'pending' && t.outcome !== 'cancelled');
    const wins = resolved.filter(t => t.outcome !== 'sl');
    const losses = resolved.filter(t => t.outcome === 'sl');
    let cumPips = 0;
    const curve = [];
    [...resolved].reverse().forEach(t => {
        cumPips += t.pnlPips ?? 0;
        curve.push(cumPips);
    });
    const pips = resolved.map(t => t.pnlPips ?? 0);
    // Current win streak
    let streak = 0;
    for (const t of list) {
        if (t.outcome === 'pending' || t.outcome === 'cancelled')
            continue;
        if (t.outcome === 'sl')
            break;
        streak++;
    }
    return {
        total: list.length,
        wins: wins.length,
        losses: losses.length,
        pending: list.filter(t => t.outcome === 'pending').length,
        winRate: resolved.length ? Math.round((wins.length / resolved.length) * 100) : 0,
        totalPips: parseFloat(pips.reduce((a, b) => a + b, 0).toFixed(1)),
        avgScore: list.length ? Math.round(list.reduce((a, t) => a + t.signal.reliabilityScore, 0) / list.length) : 0,
        avgRRR: resolved.length ? parseFloat((resolved.reduce((a, t) => a + t.signal.rrr, 0) / resolved.length).toFixed(2)) : 0,
        bestTrade: pips.length ? Math.max(...pips) : 0,
        worstTrade: pips.length ? Math.min(...pips) : 0,
        streak,
        tp1Hits: resolved.filter(t => t.outcome === 'tp1').length,
        tp2Hits: resolved.filter(t => t.outcome === 'tp2').length,
        tp3Hits: resolved.filter(t => t.outcome === 'tp3').length,
        equityCurve: curve,
    };
}
